import moment from "moment";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Card from "../component/card";
import MainLayout from "../layout/main";
import { accessiblePath } from "../lib/misc";
import { getRentals, getReport } from "../service/callAPI";
import Schema from "../type/schema";
import { DataTable } from "../component/DataTable2";
import { DateSelector } from "../component/DateSelector";
import {
	populateCustomers,
	populateProducts,
	populateRentals
} from "../service/cache";

export default function ReportPage() {
	return (
		<MainLayout pageTitle="Báo Cáo" title="Báo cáo">
			<Content />
		</MainLayout>
	);
}

const Content = () => {
	const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
	const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
	const [mounted, setMounted] = useState(false);

	const [report, setReport] = useState<Schema.Report>({
		startDate: 0,
		endDate: 0,
		revenue: 0,
		income: 0,
		deposit: 0,
		itemRented: 0,
		itemInRent: 0,
		itemLost: 0,
		refund: 0,
		numberOfRentalsByProduct: [],
		rentals: []
	});

	const [rentals, setRentals] = useState<Schema.Rental[]>([]);
	const [products, setProducts] = useState<Schema.Product[]>([]);
	const [customers, setCustomers] = useState<Schema.Customer[]>([]);
	const [refundRentals, setRefundRentals] = useState<Schema.Rental[]>([]);

	useEffect(() => {
		const fetchData = async () => {
			try {
				setMounted(false);
				const response = await getReport(
					+moment(startDate).format("X"),
					+moment(endDate).format("X")
				);
				setReport(response);

				const populatedRentals = await populateRentals(response.rentals);
				setRentals(populatedRentals);

				const populatedProducts = (await populateProducts(
					response.numberOfRentalsByProduct
				)) as Schema.Product[] & { quantity: number };

				setProducts(populatedProducts);

				const populatedCustomers = await populateCustomers(response.rentals);
				setCustomers(populatedCustomers);

				const refundRentals = await getRentals({
					page: 1,
					limit: -1,
					returnDate: {
						startDate: +moment(startDate).format("X"),
						endDate: +moment(endDate).format("X")
					}
				});
				setRefundRentals(refundRentals.rentals);
			} catch (e: any) {
				const message = e?.response?.data || e.message;
				toast.error(message);
			} finally {
				setMounted(true);
			}
		};
		fetchData();
	}, [startDate, endDate]);

	return (
		<>
			<Card title="Tổng quan">
				<div
					style={{
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "flex-start",
						gap: "16px"
					}}
				>
					<ItemInRent amount={report.itemInRent} />
					<ItemLost amount={report.itemLost} />
				</div>
			</Card>

			<DateSelector {...{ startDate, endDate, setStartDate, setEndDate }}>
				<div
					style={{
						marginTop: "16px",
						display: "flex",
						flexWrap: "wrap",
						justifyContent: "flex-start",
						gap: "16px"
					}}
				>
					<Revenue amount={report.revenue} />
					<Income amount={report.income} />
					<Deposit amount={report.deposit} />
					<Refund amount={report.refund} />
					<RefundByDueDate rentals={refundRentals} />
					<ItemRented amount={report.itemRented} />
					<RentalCount amount={report.rentals.length} />
				</div>
			</DateSelector>
			<div style={{ marginTop: "16px" }}>
				<RentalData rentals={rentals} />
			</div>
			<div style={{ marginTop: "16px" }}>
				<ProductData products={products} />
			</div>
			<div style={{ marginTop: "16px" }}>
				<CustomerData customers={customers} />
			</div>
		</>
	);
};

const RentalData = ({ rentals }: { rentals: Schema.Rental[] }) => {
	return (
		<Card title="Đơn thuê">
			<DataTable
				pagination
				filterEnabled
				csvDownload={{
					enabled: true,
					fileName: "rental",
					converter: (data) => {
						return data.map((d) => {
							return {
								id: d.id,
								formattedCreatedAt: d.formattedCreatedAt,
								formattedStartDate: d.formattedStartDate,
								formattedDueDate: d.formattedDueDate,
								formattedReturnDate: d.formattedReturnDate,
								customer: d.customer?.name,
								customerPhone: d.customer?.phone,
								status: d.isDone ? "Hoàn thành" : "Đang thuê",
								totalFee: d.totalFee,
								deposit: d.deposit,
								refund: d.actualPayment
							};
						});
					}
				}}
				columns={[
					{
						name: "ID",
						selector: (row) => row.id,
						format: (row) => <a href={`/rental/${row.id}`}>{row.id}</a>,
						sortable: true
					},
					{
						name: "Ngày tạo",
						selector: (row) => row.formattedCreatedAt,
						sortable: true
					},
					{
						name: "Ngày thuê",
						selector: (row) => row.formattedStartDate,
						sortable: true
					},
					{
						name: "Ngày hẹn trả",
						selector: (row) => row.formattedDueDate,
						sortable: true
					},
					{
						name: "Ngày thực trả",
						selector: (row) => row.formattedReturnDate,
						sortable: true
					},
					{
						name: "Tên khách hàng",
						selector: (row) => row.customerId,
						format: (row) => {
							if (row.customer) {
								return (
									<a href={`/customer/${row.customer.id}`}>
										{row.customer.name} | {row.customer.phone}
									</a>
								);
							}

							return "Not found";
						},
						sortable: true
					},
					{
						name: "Trạng thái",
						selector: (row) => row.status,
						sortable: true
					},
					{
						name: "Tổng phí",
						selector: (row) => row.totalFee,
						sortable: true
					},
					{
						name: "Cọc",
						selector: (row) => row.deposit,
						sortable: true
					},
					{
						name: "Đã trả cọc",
						selector: (row) => row.actualPayment,
						sortable: true
					}
				]}
				data={rentals}
			/>
		</Card>
	);
};

const ProductData = ({ products }: { products: Schema.Product[] }) => {
	return (
		<Card title="Sản phẩm">
			<DataTable
				pagination
				filterEnabled
				csvDownload={{
					enabled: true,
					fileName: "product",
					converter: (data) => {
						return data.map((d) => {
							return {
								id: d.id,
								sku: d.sku,
								name: d.name,
								thumbnailUrl: accessiblePath(d.thumbnailUrl),
								quantity: d.quantity,
								price: d.price
							};
						});
					}
				}}
				columns={[
					{
						name: "ID",
						selector: (row) => row.id,
						format: (row) => <a href={`/product/${row.id}`}>{row.id}</a>,
						sortable: true
					},
					{
						name: "SKU",
						selector: (row) => row.sku,
						sortable: true
					},
					{
						name: "Tên sản phẩm",
						selector: (row) => row.name,
						sortable: true
					},
					{
						name: "Ảnh",
						selector: (row) => row.thumbnailUrl,
						format: (row) => (
							<img
								src={accessiblePath(row.thumbnailUrl)}
								alt={row.name}
								loading="lazy"
								style={{
									height: "120px",
									width: "100px",
									objectFit: "cover"
								}}
							/>
						)
					},
					{
						name: "Số lần thuê",
						selector: (row) => row.quantity,
						sortable: true
					},
					{
						name: "Giá sản phẩm",
						selector: (row) => row.price,
						sortable: true
					}
				]}
				data={products}
			/>
		</Card>
	);
};

const CustomerData = ({ customers }: { customers: Schema.Customer[] }) => {
	return (
		<Card title="Khách hàng">
			<DataTable
				pagination
				filterEnabled
				csvDownload={{
					enabled: true,
					fileName: "customer",
					converter: (data) => {
						return data.map((d) => {
							return {
								id: d.id,
								formattedCreatedAt: d.formattedCreatedAt,
								name: d.name,
								phone: d.phone,
								feeSum: d.feeSum,
								depositSum: d.depositSum,
								refundSum: d.refundSum,
								rentalCount: d.rentalCount,
								itemRentedCount: d.itemRentedCount,
								lostItemsCount: d.lostItemsCount,
								formattedDob: d.formattedDob
							};
						});
					}
				}}
				columns={[
					{
						name: "ID",
						selector: (row) => row.id,
						format: (row) => <a href={`/customer/${row.id}`}>{row.id}</a>,
						sortable: true
					},
					{
						name: "Ngày tạo",
						selector: (row) => row.formattedCreatedAt,
						sortable: true
					},
					{
						name: "Tên",
						selector: (row) => row.name,
						sortable: true
					},
					{
						name: "Điện thoại",
						selector: (row) => row.phone,
						sortable: true
					},
					{
						name: "Tiền thuê",
						selector: (row) => row.feeSum,
						sortable: true
					},
					{
						name: "Tiền cọc",
						selector: (row) => row.depositSum,
						sortable: true
					},
					{
						name: "Cọc đã trả",
						selector: (row) => row.refundSum,
						sortable: true
					},
					{
						name: "Đơn đã thuê",
						selector: (row) => row.rentalCount,
						sortable: true
					},
					{
						name: "Đồ đã thuê",
						selector: (row) => row.itemRentedCount,
						sortable: true
					},
					{
						name: "Đồ làm mất",
						selector: (row) => row.lostItemsCount,
						sortable: true
					},
					{
						name: "Ngày sinh",
						selector: (row) => row.formattedDob,
						sortable: true
					}
				]}
				data={customers}
			/>
		</Card>
	);
};

const Revenue = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Doanh Thu + Cọc"
			classes="border-left-success"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const Income = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Doanh thu"
			classes="border-left-success"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const Refund = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Đã trả cọc"
			classes="border-left-success"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const RefundByDueDate = ({ rentals = [] }: { rentals: Schema.Rental[] }) => {
	const refund = rentals.reduce((acc, cur) => {
		return acc + cur.actualPayment;
	}, 0);

	return (
		<Card
			style={{ width: "200px" }}
			title="Đã trả cọc (tính theo ngày trả)"
			classes="border-left-success"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(refund)}
			</div>
		</Card>
	);
};

const Deposit = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Tiền cọc"
			classes="border-left-warning"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const ItemRented = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Đồ đã cho thuê"
			classes="border-left-primary"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const ItemInRent = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Đồ đang cho thuê"
			classes="border-left-warning"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const ItemLost = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Đồ bị mất"
			classes="border-left-danger"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

const RentalCount = ({ amount = 0 }: { amount: number }) => {
	return (
		<Card
			style={{ width: "200px" }}
			title="Tổng đơn thuê"
			classes="border-left-info"
		>
			<div className="h5 mb-0 font-weight-bold text-gray-800">
				{withSeparator(amount)}
			</div>
		</Card>
	);
};

function withSeparator(number: number) {
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
